import React, { memo, useState } from 'react'
import ReactDOM from 'react-dom'

import PropTypes from 'prop-types'
import moment from 'moment'
import { Draggable } from '@hello-pangea/dnd'
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core'
import clsx from 'clsx'
import {
  MoreVertical as MoreVerticalIcon,
  User as UserIcon,
} from 'react-feather'

import { KanbanCardMenu, KanbanCardDeleteDialog } from './components'

import helpers from 'helpers'

import usePermissions from 'hooks/usePermissions'
import useAuth from 'hooks/useAuth'
import useSourceOrigin from 'hooks/useSourceOrigin'

import constants from 'constants/index'
import useStyles from './styles'

// A coluna é uma camada isolada (isolation: isolate, para o cabeçalho fixo): sem portal, o card arrastado para a
// direita fica atrás da coluna seguinte, que é pintada depois. Fora da coluna ele passa por cima de todas.
const inPortalWhileDragging = (isDragging, element) =>
  isDragging ? ReactDOM.createPortal(element, document.body) : element

const KanbanCard = ({ card, index, columnRole, onEdit, onDelete }) => {
  const {
    PRIORITY_LABELS,
    KANBAN_COLUMN_ROLE_FINALIZED,
    SEVERE,
    HIGH,
    MEDIUM,
    LOW,
  } = constants.activities

  const classes = useStyles()
  const permissions = usePermissions()

  const { user } = useAuth()
  const { hasSourceOrigin, openSourceOrigin } = useSourceOrigin()
  const isReadOnly = Boolean(card.targetCompanyId)
  const isTaskOwner =
    card.authorId === user?.id ||
    card.responsibles?.some((r) => r.id === user?.id)
  const canDrag =
    !isReadOnly &&
    (permissions.some([
      constants.permissions.KANBAN_TASKS.MANAGE,
      constants.permissions.KANBAN_TASKS.FINALIZE,
    ]) ||
      isTaskOwner)
  const [anchorEl, setAnchorEl] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)

  const handleOpenMenu = (e) => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
  }

  const handleCloseMenu = () => setAnchorEl(null)

  const handleDeleteConfirmed = async () => {
    setConfirmDeleteOpen(false)
    setIsDeleting(true)
    try {
      if (onDelete) await onDelete(card)
    } finally {
      setIsDeleting(false)
    }
  }

  const isOverdue =
    card.dueDate &&
    moment(card.dueDate).isBefore(moment().startOf('day')) &&
    columnRole !== KANBAN_COLUMN_ROLE_FINALIZED

  const responsiblesText = card.responsibles?.length
    ? card.responsibles.map((r) => r.name).join(', ')
    : null
  const responsiblesTruncated = responsiblesText
    ? helpers.truncateUIText(responsiblesText, 30)
    : null

  const borderClass = {
    [SEVERE]: classes.borderSevere,
    [HIGH]: classes.borderHigh,
    [MEDIUM]: classes.borderMedium,
    [LOW]: classes.borderLow,
  }[card.priority]

  const priorityTextClass = {
    [SEVERE]: classes.prioritySevere,
    [HIGH]: classes.priorityHigh,
    [MEDIUM]: classes.priorityMedium,
    [LOW]: classes.priorityLow,
  }[card.priority]

  return (
    <>
      <Draggable
        draggableId={String(card.id)}
        index={index}
        isDragDisabled={!canDrag}
      >
        {(provided, snapshot) => {
          const cardElement = (
            <Paper
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              className={clsx(classes.card, borderClass, {
                [classes.cardDragging]: snapshot.isDragging,
                [classes.cardReadOnly]: isReadOnly,
              })}
              elevation={0}
              onClick={() => {
                if (onEdit) onEdit(card)
              }}
            >
              <Box className={classes.cardHeader}>
                <Box
                  display="flex"
                  alignItems="center"
                  className={classes.cardIdRow}
                >
                  <Typography className={classes.cardId}>
                    #{String(card.id).padStart(5, '0')}
                  </Typography>
                  {isReadOnly && (
                    <Chip
                      label="Encarregado (DPO)"
                      size="small"
                      className={classes.dpoChip}
                    />
                  )}
                  {card.autoManaged && (
                    <Chip
                      label="Automática"
                      size="small"
                      className={classes.autoChip}
                    />
                  )}
                </Box>
                {isDeleting ? (
                  <CircularProgress size={16} />
                ) : (
                  <IconButton
                    size="small"
                    className={classes.menuButton}
                    onClick={handleOpenMenu}
                  >
                    <MoreVerticalIcon size={16} />
                  </IconButton>
                )}
              </Box>

              {card.name.length > 60 ? (
                <Tooltip title={card.name}>
                  <Typography className={classes.cardTitle}>
                    {helpers.truncateUIText(card.name, 60)}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography className={classes.cardTitle}>
                  {card.name}
                </Typography>
              )}

              {card.priority && (
                <Typography className={classes.priorityLabel}>
                  Prioridade:{' '}
                  <span className={priorityTextClass}>
                    {PRIORITY_LABELS[card.priority]}
                  </span>
                </Typography>
              )}

              <Box className={classes.cardFooter}>
                <Box className={classes.responsible}>
                  <Box className={classes.iconFixed}>
                    <UserIcon size={13} />
                  </Box>
                  <span>
                    {responsiblesText ? (
                      responsiblesText.length > 30 ? (
                        <Tooltip title={responsiblesText}>
                          <span>{responsiblesTruncated}</span>
                        </Tooltip>
                      ) : (
                        responsiblesText
                      )
                    ) : (
                      '—'
                    )}
                  </span>
                </Box>
                {card.dueDate && (
                  <Typography
                    className={
                      isOverdue ? classes.deadlineOverdue : classes.deadline
                    }
                  >
                    {helpers.formatters.date(card.dueDate)}
                  </Typography>
                )}
              </Box>
            </Paper>
          )

          return inPortalWhileDragging(snapshot.isDragging, cardElement)
        }}
      </Draggable>

      <KanbanCardMenu
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        onOpen={() => {
          if (onEdit) onEdit(card)
        }}
        onDelete={() => setConfirmDeleteOpen(true)}
        onViewSource={() => openSourceOrigin(card)}
        showViewSource={hasSourceOrigin(card)}
        hideDelete={isReadOnly || card.autoManaged}
      />

      <KanbanCardDeleteDialog
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        card={card}
        onConfirm={handleDeleteConfirmed}
        isDeleting={isDeleting}
      />
    </>
  )
}

KanbanCard.propTypes = {
  card: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  columnRole: PropTypes.string,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
}

export default memo(KanbanCard)
